import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../images/haqdarshak-logo.png';
import { logoutUser } from '../utils/auth';

const SummaryHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [executionDate, setExecutionDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://api-project360-insights.haqdarshak.com/api/last-execution-date/');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        setExecutionDate(data.last_execution_date);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch execution date:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString.replace(' AM', ''));
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  // Logout now waits for the backend to revoke the token before redirecting, so
  // the button reports progress instead of looking dead for a moment.
  const handleLogout = () => {
    setIsLoggingOut(true);
    logoutUser(); // revokes server-side, clears session + MSAL, redirects to "/"
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('Project-Level')) return 'Project Level Analytics';
    if (path.includes('ProjectSummary')) return 'Project Summary';
    if (path.includes('Dashboard')) return 'Dashboard';
    return 'Project Summary';
  };

  return (
    <>
      <nav className="fixed top-0 left-0 w-full bg-white shadow-lg z-50">
        {/* Main Header */}
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Brand Section */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img 
                  src={logo} 
                  alt="Logo" 
                  className="h-10 w-auto object-contain"
                />
                <div className="h-8 w-px bg-gray-300 hidden sm:block"></div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent hidden sm:block">
                  PROJECT360 DASHBOARD
                </h1>
              </div>
            </div>

            {/* Page Title - Desktop */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-pink-400 to-pink-700 px-6 py-1.5 rounded-full shadow-sm">
                <span className="text-white font-semibold text-sm tracking-wide">
                  {getPageTitle()}
                </span>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              {/* Last Update Badge */}
              <div className="hidden lg:flex items-center bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-500 border-t-transparent"></div>
                    <span className="text-xs text-gray-500">Updating...</span>
                  </div>
                ) : error ? (
                  <div className="flex items-center space-x-2">
                    <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-red-500">Update failed</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-gray-600">
                      Last updated: <span className="font-medium">{formatDate(executionDate)}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="hidden sm:inline">{isLoggingOut ? "Signing out…" : "Logout"}</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Page Title */}
          <div className="md:hidden mt-3 pt-3 border-t border-gray-100">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-1.5 rounded-full inline-block">
              <span className="text-white font-semibold text-xs tracking-wide">
                {getPageTitle()}
              </span>
            </div>
          </div>

          {/* Mobile Last Update */}
          <div className="lg:hidden mt-2">
            <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200 w-fit">
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-xs text-gray-500">Updating...</span>
                </div>
              ) : error ? (
                <div className="flex items-center space-x-2">
                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs text-red-500">Update failed</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs text-gray-600">
                    Updated: <span className="font-medium">{formatDate(executionDate)}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Shadow Gradient */}
        <div className="h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="fixed top-16 left-0 w-full bg-white shadow-lg z-40 md:hidden animate-slideDown">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Current Page</span>
              <span className="text-sm font-semibold text-gray-800">{getPageTitle()}</span>
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>{isLoggingOut ? "Signing out…" : "Logout"}</span>
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default SummaryHeader;